import * as React from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { addModifiers } from '../../shared/helpers';
import { isAction } from '../../shared/helpers/fetch-helper';
import { localeData } from '../../locales/data';
import './EditableOsmosisPointsCell.scss';

interface EditableOsmosisPointsCellProps {
  value: number | null | undefined;
  alphaId: string;
  onSave: (alphaId: string, value: number | null) => Promise<any>;
  row: any;
  intl: IntlShape;
}

interface EditableOsmosisPointsCellState {
  isEditing: boolean;
  editValue: string;
  currentValue: number | null | undefined;
  hasError: boolean;
  errorMessage: string;
  isSaving: boolean;
  hasSuccess: boolean;
}

const MIN_VALUE = 1;
const MAX_VALUE = 100000;
const ERROR_DISPLAY_DURATION = 5000;
const SUCCESS_DISPLAY_DURATION = 2000;

export class EditableOsmosisPointsCell extends React.Component<EditableOsmosisPointsCellProps, EditableOsmosisPointsCellState> {
  private inputRef: React.RefObject<HTMLInputElement | null>;

  constructor(props: EditableOsmosisPointsCellProps) {
    super(props);
    this.inputRef = React.createRef();
    this.state = {
      isEditing: false,
      editValue: this.formatValueForDisplay(props.value),
      currentValue: props.value,
      hasError: false,
      errorMessage: '',
      isSaving: false,
      hasSuccess: false,
    };
  }

  componentDidUpdate(prevProps: EditableOsmosisPointsCellProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({
        currentValue: this.props.value,
        editValue: this.formatValueForDisplay(this.props.value),
        ...this.getResetState(),
      });
    }
  }

  private formatValueForDisplay = (value: number | null | undefined): string => {
    return value !== null && value !== undefined ? value.toString() : '';
  };

  private getResetState = () => ({
    hasError: false,
    errorMessage: '',
    hasSuccess: false,
  });

  private validateInput = (value: string): number | null => {
    if (value.trim() === '') return null;

    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < MIN_VALUE || numValue > MAX_VALUE) {
      throw new Error(
        this.props.intl.formatMessage({
          id: 'edit.properties.osmosisPoints.range',
          defaultMessage: localeData.en['edit.properties.osmosisPoints.range'],
        })
      );
    }

    return numValue;
  };

  private showValidationError = (errorMessage: string) => {
    this.setState({
      isEditing: false,
      editValue: this.formatValueForDisplay(this.state.currentValue),
      hasError: true,
      errorMessage,
      hasSuccess: false,
      isSaving: false,
    });

    setTimeout(() => {
      this.setState(this.getResetState());
    }, ERROR_DISPLAY_DURATION);
  };

  private showSuccess = (newValue: number | null) => {
    this.setState({
      isEditing: false,
      currentValue: newValue,
      editValue: this.formatValueForDisplay(newValue),
      isSaving: false,
      hasError: false,
      errorMessage: '',
      hasSuccess: true,
    });

    setTimeout(() => {
      this.setState({ hasSuccess: false });
    }, SUCCESS_DISPLAY_DURATION);
  };

  private showBackendError = (error: any) => {
    const errorMessage =
      error?.message ||
      this.props.intl.formatMessage({
        id: 'edit.properties.osmosisPoints.failed',
        defaultMessage: localeData.en['edit.properties.osmosisPoints.failed'],
      });

    this.setState({
      isEditing: false,
      editValue: this.formatValueForDisplay(this.state.currentValue),
      isSaving: false,
      hasError: true,
      errorMessage,
    });

    setTimeout(() => {
      this.setState(this.getResetState());
    }, ERROR_DISPLAY_DURATION);
  };

  private focusAndSelectInput = () => {
    setTimeout(() => {
      if (this.inputRef.current) {
        this.inputRef.current.focus();
        this.inputRef.current.select();
      }
    }, 0);
  };

  handleCellClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    this.setState({ isEditing: true }, this.focusAndSelectInput);
  };

  handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ editValue: e.target.value });
  };

  handleInputBlur = () => {
    this.saveValue();
  };

  handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      this.saveValue();
    } else if (e.key === 'Escape') {
      this.cancelEdit();
    }
  };

  saveValue = async () => {
    const { editValue, currentValue } = this.state;

    try {
      const newValue = this.validateInput(editValue);

      if (newValue === currentValue || (newValue == null && currentValue == null)) {
        this.setState({
          isEditing: false,
          ...this.getResetState(),
        });
        return;
      }

      this.setState({
        isSaving: true,
        ...this.getResetState(),
      });

      const result = await this.props.onSave(this.props.alphaId, newValue);
      if (isAction(result, 'RESPONSE_UPDATE_ALPHA_PROPERTIES_FAILURE')) {
        this.showBackendError(result);
      } else {
        this.showSuccess(newValue);
      }
    } catch (error: any) {
      if (error.message.includes('edit.properties.osmosisPoints.range')) {
        this.showValidationError(error.message);
      } else {
        this.showBackendError(error);
      }
    }
  };

  cancelEdit = () => {
    this.setState({
      isEditing: false,
      editValue: this.formatValueForDisplay(this.state.currentValue),
      ...this.getResetState(),
    });
  };

  private renderEditingState = () => {
    const { editValue, isSaving } = this.state;

    return (
      <div className={this.getCellClassName()} onClick={e => e.stopPropagation()}>
        <input
          ref={this.inputRef}
          type="number"
          min={MIN_VALUE}
          max={MAX_VALUE}
          step="1"
          value={editValue}
          onChange={this.handleInputChange}
          onBlur={this.handleInputBlur}
          onKeyDown={this.handleKeyPress}
          className="table__input"
          placeholder={`${MIN_VALUE}-${MAX_VALUE}`}
          disabled={isSaving}
        />
        <span className={isSaving ? 'alphas-list-table__cell-saving-icon' : 'alphas-list-table__cell-edit-icon'}>
          {isSaving ? '⟳' : '✎'}
        </span>
      </div>
    );
  };

  private renderDisplayState = () => {
    const { currentValue } = this.state;
    const displayValue = this.formatValueForDisplay(currentValue);

    return (
      <div className={this.getCellClassName()} onClick={this.handleCellClick} title={this.getTooltipText()}>
        <span className="alphas-list-table__cell-display-text">{displayValue}</span>
        {this.renderStateIcon()}
      </div>
    );
  };

  private getCellClassName = () => {
    const { hasError, hasSuccess } = this.state;
    const modifiers = ['osmosis-points', 'editable'];

    if (hasError) modifiers.push('error');
    if (hasSuccess) modifiers.push('success');

    return addModifiers('alphas-list-table__cell-content', modifiers);
  };

  private getTooltipText = () => {
    const { hasError, errorMessage, hasSuccess } = this.state;

    if (hasError) return errorMessage;
    if (hasSuccess)
      return this.props.intl.formatMessage({
        id: 'edit.properties.osmosisPoints.success',
        defaultMessage: localeData.en['edit.properties.osmosisPoints.success'],
      });
    return this.props.intl.formatMessage(
      {
        id: 'edit.properties.osmosisPoints.tooltip',
        defaultMessage: localeData.en['edit.properties.osmosisPoints.tooltip'],
      },
      { min: MIN_VALUE, max: MAX_VALUE }
    );
  };

  private renderStateIcon = () => {
    const { hasError, hasSuccess } = this.state;

    if (hasError) {
      return <span className="alphas-list-table__cell-error-icon">⚠</span>;
    }

    if (hasSuccess) {
      return <span className="alphas-list-table__cell-success-icon">✓</span>;
    }

    return <span className="alphas-list-table__cell-edit-icon">✎</span>;
  };

  render() {
    const { isEditing } = this.state;
    return isEditing ? this.renderEditingState() : this.renderDisplayState();
  }
}

export default injectIntl(EditableOsmosisPointsCell);
